import { useState } from 'react';
import { useParams, useNavigate, Link, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AddressInput, type AddressData } from '@/components/ui/address-input';
import { createCustomer } from '@vn-propane/shared/client';
import type { UserOrganization } from '@vn-propane/shared/client';
import { ArrowLeft } from 'lucide-react';

type OutletContext = {
  organization: UserOrganization;
};

export default function NewCustomer() {
  const { id: orgId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['admin', 'common']);
  const { organization } = useOutletContext<OutletContext>();
  const [saving, setSaving] = useState(false);

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [addressData, setAddressData] = useState<AddressData | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgId || !fullName) return;

    setSaving(true);
    const result = await createCustomer({
      path: { org_id: orgId },
      body: {
        fullName,
        phone: phone || null,
        email: email || null,
        addressId: addressData?.address?.id || null,
      },
    });

    if (result.error) {
      console.error('Failed to create customer:', result.error);
      setSaving(false);
      return;
    }

    if (result.data) {
      navigate(`/organizations/${orgId}/customers/${result.data.data.id}`);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <Link
          to={`/organizations/${orgId}/customers`}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('customers.backToCustomers')}
        </Link>
      </div>

      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">{t('customers.addCustomer')}</h1>
          <p className="text-muted-foreground">
            {t('customers.addNewCustomer')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="full_name">{t('customers.fullNameRequired')}</Label>
              <Input
                id="full_name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">{t('customers.phone')}</Label>
              <Input
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+84 123 456 789"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t('customers.email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="customer@example.com"
              />
              <p className="text-sm text-muted-foreground">
                {t('customers.emailHelp')}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">{t('customers.address')}</h3>
            <AddressInput
              organizationId={orgId || ''}
              value={addressData}
              onChange={setAddressData}
              defaultCountry={organization.default_country}
            />
          </div>

          <div className="flex gap-4">
            <Button type="submit" disabled={!fullName || saving}>
              {saving ? t('customers.creating') : t('customers.createCustomer')}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/organizations/${orgId}/customers`)}
            >
              {t('common:buttons.cancel')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
